<template>
  <div class="flex flex-column gap-3">
    <attachment-view @update="$emit('update')" />

    <div class="flex flex-column gap-3">
      <div class="flex justify-content-between">
        <h3>Digital CDPH 530</h3>
        <div class="flex gap-2">
          <a-button v-if="confirmation" icon="download" type="primary" @click="downloadConfirmation">
            Download
          </a-button>

          <a-button icon="plus" type="primary" :loading="loadingGeneration" @click="generateConfirmation">
            {{ confirmation ? 'Regenerate' : 'Generate' }}
          </a-button>
          <a-button
            v-if="confirmation && !confirmation?.signature"
            icon="form"
            type="primary"
            @click="visibleSignModal = true"
          >
            Sign
          </a-button>
        </div>
      </div>
      <p class="m-0">
        Review the manual attachment and digital CDPH-530 form for this shift. Generate the digital form if it is
        missing, then have a supervisor sign it.
      </p>
      <template v-if="confirmation">
        <conformation-sign-modal
          :visible.sync="visibleSignModal"
          :conformation_id="confirmation.id"
          @update="$emit('update')"
        />

        <div class="flex justify-content-end">
          <a-descriptions :column="1" size="small">
            <a-descriptions-item label="Status">
              <a-tag class="m-0" :color="confirmationStatus.color">
                {{ confirmationStatus.title }}
              </a-tag></a-descriptions-item
            >
            <a-descriptions-item label="Supervisor">
              <grouped-associate-info
                v-if="confirmation?.supervisor"
                :first-name="confirmation.supervisor.first_name"
                :last-name="confirmation.supervisor.last_name"
                :photo-url="confirmation.supervisor.photo_url"
                :associate-number="confirmation.supervisor.associate_number"
                :role="confirmation.supervisor.role.name"
                :unverified="!confirmation.supervisor.is_verified"
              />
              <span v-else>-</span>
            </a-descriptions-item>
          </a-descriptions>
        </div>
        <div v-if="url" class="w-full" :style="{ maxWidth: '99%' }">
          <pdf-viewer class="m-0" :file="url" />
        </div>
      </template>

      <h4 v-else class="text-center">Shifts do not have a Digital CDPH 530 Form.</h4>
    </div>
  </div>
</template>

<script>
import { Button, Descriptions, notification, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import moment from 'moment-timezone'
import shiftsHelper from '@/helpers/shifts'
import shiftViewAttachmentsVue from './shift-view-attachments.vue'
import PdfViewer from '@/components/entities/viewer/pdf-viewer.vue'
import ShiftViewConformationSign from './shift-view-conformation-sign.vue'
import util from '@/util'

const { mapState: shiftState, mapActions: shiftActions } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-button': Button,
    'a-tag': Tag,
    'grouped-associate-info': GroupedAssociateInfo,
    'attachment-view': shiftViewAttachmentsVue,
    'pdf-viewer': PdfViewer,
    'conformation-sign-modal': ShiftViewConformationSign
  },
  mixins: [shiftsHelper],

  data() {
    return {
      timeZone: this.$store.state.applicationState.timeZone,
      dateTimeFormat: this.$store.state.applicationState.dateTimeFormat,

      loadingGeneration: false,

      url: '',

      visibleSignModal: false
    }
  },
  computed: {
    ...shiftState({
      confirmation: state => state.shift.shift.shift_confirmation_form,
      shift: state => state.shift.shift
    }),

    signature() {
      if (this.confirmation) {
        return {
          ...this.confirmation.signature,
          signed_date: this.confirmation.confirmed_at
        }
      }

      return undefined
    },

    confirmationStatus() {
      if (this.confirmation?.signature) {
        return {
          title: 'Signed',
          color: '#10b981'
        }
      } else {
        return {
          title: 'Not Signed',
          color: '#f59e0b'
        }
      }
    }
  },
  watch: {
    confirmation: {
      async handler(newVal) {
        if (newVal) {
          try {
            const payload = {
              id: newVal.id
            }

            const link = await this.getShiftConfirmation(payload)

            this.url = link
          } catch (error) {
            this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...shiftActions(['getShiftConfirmation', 'generateShiftConfirmation']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    formatDate(date) {
      return moment.utc(date).tz(this.timeZone).format(this.dateTimeFormat)
    },

    async downloadConfirmation() {
      if (!this.confirmation.id) {
        this.showNotification('error', 'Error', 'This confirmation can not be downloaded')
      }

      try {
        const payload = {
          id: this.confirmation.id
        }

        const link = await this.getShiftConfirmation(payload)

        util.downloadFile(link)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    async generateConfirmation() {
      this.loadingGeneration = true
      try {
        const payload = {
          shifts: [this.shift.id]
        }

        await this.generateShiftConfirmation(payload)
        this.$emit('update')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingGeneration = false
      }
    }
  }
}
</script>

<style scoped>
.signature-description {
  background-color: var(--error-color);
}
</style>
